import { Component, OnInit, EventEmitter, Input, Output, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewChecked, DoCheck, OnDestroy, inject, effect, Inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { Map as LMap, MapOptions, LatLngBounds, Layer, LayerGroup, LatLng, LeafletEvent, Control, Icon, latLngBounds } from 'leaflet';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-draw';
import 'leaflet-tile-loading-progress-control';
import 'leaflet-geometryutil';
import { cloneDeep } from 'lodash';
import { Observable, Subscription } from 'rxjs';
// Library: core
import { Command } from '../../core/types/command.enum';
import { AUTH_PROVIDER, NAVBAR_PROVIDER, FILTER_PROVIDER, PROJECT_PROVIDER, CONFIRM_SERVICE } from '../../core/tokens';
import { AuthProvider, NavbarProvider, FilterProvider, ProjectProvider, IConfirmService } from '../../core/interfaces';
import { Mutex } from '../../core/utils/mutex';
// Library: map types, utils, services, controls, handlers
import { LayerDetails, MapStatus, OptionalLayer } from '../types';
import { getBoundsForPolygon, isPointInPolygon } from '../utils';
import { MapStatusService, DrawingService, DrawingBridge, MapSelectionService, SelectionBridge, GpkgRenderingService, GpkgRenderingBridge, PointRenderingService, PointRenderingBridge, PolygonRenderingService, PolygonRenderingBridge, MapLayerFactoryService, LayerFactoryBridge, FilterCoordinationService, FreehandRegionService, LayerOrderingService, WmsInfoService } from '../services';
import { CustomZoomControl, SelectionControl, SelectionMode } from '../controls';
import { MapMarker, MapPolygon, ProjectAssignmentHandler, PROJECT_ASSIGNMENT_HANDLER, DataLifecycleHandler, DATA_LIFECYCLE_HANDLER, MapNavigationHandler, MAP_NAVIGATION_HANDLER } from '../handlers';
// Library: map layer/service files
import { LayerService, Region } from '../services/layer.service';
import { OfflineLayerService } from '../services/offline-layer.service';
// Library: map controls
import { BoxSelectionControl } from '../controls/box-selection-control.component';
import { PolygonSelectionControl } from '../controls/polygon-selection-control.component';
import { MapControlsComponent } from '../controls/map-controls/map-controls.component';
// Library: map project components
import { ProjectManagerComponent } from '../project/project-manager.component';
// Library: map project
import { ProjectPointsService } from '../project/project-points.service';
// Library: services
import { GpsService } from '../../services/gps.service';
import { StorageService } from '../../services/storage/storage.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { SelectionService } from '../../services/selection.service';
// Library: map types

Icon.Default.imagePath = 'assets/leaflet/';

@Component({
    selector: 'bp-map',
    standalone: true,
    imports: [
        CommonModule,
        LeafletModule,
        MapControlsComponent,
        ProjectManagerComponent
    ],
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.css'],
})
export class MapComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked, DoCheck {
  @Input() seedRequests = new Set<string>();
  @Input() online = true;
  @Input() mapStatus?: MapStatus;
  @Input() layers?: LayerDetails[] = [];
  @Input() visible = false;
	@Input() persistentBounds = false;
	/** Zeigt nur Layer-Control (ohne vollständigen ProjectManager) */
	@Input() showLayersControl = false;
	/** Eingeschränkter Modus: keine Toolbar, kein Projektmenü, kein Punkt-Kontextmenü */
	@Input() restrictedMode = false;

	// #region Punkte-Inputs (generisch)
	@Input() points?: MapMarker[];
	@Input() pointsFilter?: boolean[];
	// #endregion

	// #region Polygon-Inputs (generisch)
	@Input() polygons?: MapPolygon[];
	@Input() highlightPolygonIds?: Set<string>;
	// #endregion

	// #region Draggable Points
	@Input() draggablePoints = false;
	// #endregion

  @Input() draggablePoint?: LatLng;
  @Input() center?: LatLng;
  @Input() repositionMode = false;

  @Output() map$ = new EventEmitter<LMap>();
  @Output() zoom$ = new EventEmitter<number>();
  @Output() mapStatusChange: EventEmitter<MapStatus> = new EventEmitter<MapStatus>(true);
  @Output() draggablePointChange: EventEmitter<LatLng> = new EventEmitter<LatLng>(true);
  @Output() repositionConfirmed = new EventEmitter<LatLng>();
  @Output() repositionCancelled = new EventEmitter<void>();
  @Output() attachementAdded = new EventEmitter<void>(true);
	// #region Punkt-Outputs (App-spezifisch)
	@Output() pointClicked = new EventEmitter<MapMarker>(true);
	@Output() pointDeleted = new EventEmitter<void>(true);
	@Output() pointDragged = new EventEmitter<{ id: string; latLng: LatLng; revert: () => void }>(true);
	// #endregion

	// #region Polygon-Outputs
	@Output() polygonClicked = new EventEmitter<MapPolygon>(true);
	// #endregion

	// #region Marker-Highlight
	@Input() highlightMarkerId?: string;
	private highlightMarkerCircle?: L.CircleMarker;
	// #endregion

	@ViewChild("mapDiv") mapDiv!: ElementRef<HTMLDivElement>;

  private scheduleLayerChange = true;
  private layerChangeTimeout: any = null;
  private regionsChangeTimeout: any = null;
  private isLoadingLayers = false;
  private schedulePointsChange = true;
  private schedulePolygonsChange = true;
  private scheduleDraggablePointChange = true;
  private draggablePointMarker?: L.Marker;
  maxZoom = 18;


  public map!: LMap;
  mapLayers: any = {};
  pointLayer?: LayerGroup;
	// pointsLayer und gridLayer → PointRenderingService
	private _currentPosition?: L.Marker;
	get currentPosition(): L.Marker {
		if (!this._currentPosition) {
			this._currentPosition = L.marker(new LatLng(Infinity, Infinity), {
				icon: this.createGpsIcon(null)
			});
		}
		return this._currentPosition;
	}

	private createGpsIcon(heading: number | null): L.DivIcon {
		const arrowStyle = heading != null
			? `transform: rotate(${heading}deg); opacity: 1;`
			: `opacity: 0;`;
		const html = `
			<div style="position: relative; width: 40px; height: 40px;">
				<img src="assets/img/crosshair.svg" style="width: 40px; height: 40px;">
				<div class="gps-heading-arrow" style="
					position: absolute;
					top: -12px;
					left: 50%;
					transform-origin: center 32px;
					${arrowStyle}
					pointer-events: none;
				">
					<svg width="16" height="16" viewBox="0 0 16 16" style="margin-left: -8px;">
						<polygon points="8,0 14,14 8,10 2,14" fill="#2196F3" stroke="#1565C0" stroke-width="1"/>
					</svg>
				</div>
			</div>
		`;
		return L.divIcon({
			html,
			className: '',
			iconSize: [40, 40],
			iconAnchor: [20, 20]
		});
	}

	updateGpsHeading(heading: number | null): void {
		if (this._currentPosition) {
			const icon = this._currentPosition.getElement();
			if (icon) {
				const arrow = icon.querySelector('.gps-heading-arrow') as HTMLElement;
				if (arrow) {
					if (heading != null) {
						arrow.style.transform = `rotate(${heading}deg)`;
						arrow.style.opacity = '1';
					} else {
						arrow.style.opacity = '0';
					}
				}
			}
		}
	}
  // Anzahl gefilterter Punkte für Map-Controls
  get filteredCount(): number {
    return this.pointsFilter?.filter(f => f).length ?? 0;
  }

  regions?: L.FeatureGroup;

  // pointLabelLayer, pointLabelRects, projectPointIds, assignedPointIds → PointRenderingService

  options!: MapOptions;

	private storage = inject(StorageService);
	public oms = inject(OfflineModeService);
	// markerHandler, leafletMarkers → PointRenderingService
	private projectHandler = inject(PROJECT_ASSIGNMENT_HANDLER, { optional: true }) as ProjectAssignmentHandler | null;
	private dataLifecycleHandler = inject(DATA_LIFECYCLE_HANDLER, { optional: true }) as DataLifecycleHandler | null;
	private navigationHandler = inject(MAP_NAVIGATION_HANDLER, { optional: true }) as MapNavigationHandler | null;


	private mapSize!: DOMRect;
	private invalidateMapSize = false;
	private pendingSeedRequest: (0|1|2) | null = null;
	private pendingSeedLayerRequest: string | null = null;
	
	C = Command;
	
	private zoomControl = new CustomZoomControl({position: 'topleft'});
	private selectionControl = new SelectionControl({ position: 'topleft' });

	private boxControl?: BoxSelectionControl;
	private polygonControl?: PolygonSelectionControl;

	// Panel-States (Angular-basiert statt Leaflet-Control)
	projectPanelCollapsed = false;

	toggleProjectPanel() {
		this.projectPanelCollapsed = !this.projectPanelCollapsed;
		this.storage.setSync('projectPanelCollapsed', this.projectPanelCollapsed);
	}
	layersPanelCollapsed = true;

	// #region Punkt-Kontextmenü (App-spezifisch)
	contextMenuMarker: MapMarker | null = null;
	contextMenuPosition: { x: number, y: number } | null = null;
	private contextMenuHighlight?: L.CircleMarker;
	private lastClickedPointId: string | null = null;
	// #endregion

	// #region Polygon-Kontextmenü
	contextMenuPolygon: MapPolygon | null = null;
	contextMenuPolygonPosition: { x: number, y: number } | null = null;
	private contextMenuPolygonHighlight?: L.Polygon[];
	private lastClickedPolygonId: string | null = null;
	// #endregion

	// Panel-Positionen für Drag & Drop
	panelPosition: { x: number | null, y: number } = { x: null, y: 50 };
	layersPanelPosition: { x: number | null, y: number } = { x: null, y: 50 };
	private draggingPanel: HTMLElement | null = null;
	private dragOffset = { x: 0, y: 0 };

	// Map-Drag-Tracking (verhindert Kontextmenü bei Drag über Punkte)
	private mapWasDragged = false;

	// WMS-Info-Tool Helpers
	objectKeys = Object.keys;

	get hasVisibleWmsLayers(): boolean {
		return this.wmsInfoService.hasVisibleInfoLayers();
	}


	private _subscriptions = new Map<string, Subscription>();
	private authMutex = new Mutex();
	private destroyRef = inject(DestroyRef);
		
  constructor(
		private gps: GpsService,
		@Inject(AUTH_PROVIDER) public aus: AuthProvider,
		private ls: LayerService,
		@Inject(FILTER_PROVIDER) private fs: FilterProvider,
		@Inject(NAVBAR_PROVIDER) private ns: NavbarProvider,
		private mapStatusService: MapStatusService,
		private os: OfflineLayerService,
		@Inject(PROJECT_PROVIDER) public ps: ProjectProvider,
		private pps: ProjectPointsService,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService,
		public selectionService: SelectionService,
		public drawingService: DrawingService,
		public mapSelectionService: MapSelectionService,
		public gpkgRenderingService: GpkgRenderingService,
		public pointRenderingService: PointRenderingService,
		public polygonRenderingService: PolygonRenderingService,
		public mapLayerFactoryService: MapLayerFactoryService,
		public filterCoordinationService: FilterCoordinationService,
		public freehandRegionService: FreehandRegionService,
		public layerOrderingService: LayerOrderingService,
	public wmsInfoService: WmsInfoService
  ) {
		this.storage.get('projectPanelCollapsed').then(val => {
			if (val != null) this.projectPanelCollapsed = !!val;
		});
		this.aus.authChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onAuthChanged());
		this.gps.position().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ll: LatLng) => this.currentPosition.setLatLng(ll));
		this.gps.heading().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((heading: number | null) => this.updateGpsHeading(heading));
		this.ls.offlineModeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((offline: boolean) => {
			this._updateProjectArea();
			// Cache leeren damit WMS-Layer nicht wiederverwendet werden
			this.ls.clearLayerCache();
			// Layer neu erstellen damit WMS zu OfflineGridLayer wird (oder umgekehrt)
			this.onLayersChanged();
		});
		// Nur layersChanged subscriben - layersDefined wird gleichzeitig emittiert
		this.ls.layersChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onLayersChanged());
		this.ls.layerVisibilityChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onLayerVisibilityChanged());
		this.ls.layerOrderChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((order: string[]) => this.layerOrderingService.onLayerOrderChanged(order));
		this.ls.boundsFromMapRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onBoundsRequested());
		this.ls.customBoundsFromMapRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onCustomBoundsRequested());
		this.ls.polygonFromMapRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onPolygonRequested());
		this.ls.freehandFromMapRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.freehandRegionService.onFreehandRegionRequested());
		this.ls.regionsChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onRegionsChanged());
		this.ls.regionNamesChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onRegionsChanged());
		this.ls.regionClicked.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((r: LatLngBounds) => this.onRegionClicked(r));
		this.ls.zoomToLayerRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((layerName: string) => this.onZoomToLayer(layerName));
		this.ls.pointBoundsSelectionRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onPointBoundsRequested());
		this.ls.projectPointsChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids: Set<string>) => this.onProjectPointsChanged(ids));
		this.ls.assignedPointsChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids: Set<string>) => this.onAssignedPointsChanged(ids));

		// Navigation zu Punkt
		this.mapStatusService.panTo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ center, zoom }) => {
			if (this.map) {
				if (zoom) {
					this.map.setView(center, zoom, { animate: true });
				} else {
					this.map.panTo(center, { animate: true });
				}
			}
		});

		// Zeichnungslayer-Events an DrawingService delegieren
		this.drawingService.initSubscriptions();
		this.ls.gpkgLayerZoomRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fullLayerName: string) => this.gpkgRenderingService.zoomToGpkgLayer(fullLayerName, this.mapLayers));
		this.ls.gridZoomRequested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.zoomToGrid());
		this.ls.pointsLayerVisibilityChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((visible: boolean) => this.setPointsLayerVisibility(visible));
		this.ls.polygonsLayerVisibilityChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((visible: boolean) => this.setPolygonsLayerVisibility(visible));

		this.fs.filtersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onFilterChanged());

		// Daten-Lifecycle-Events (Checkout/Checkin) via Handler
		if (this.dataLifecycleHandler) {
			this.dataLifecycleHandler.checkedOut$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onLayersChanged());
			this.dataLifecycleHandler.checkedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { this.ps.clearProject().then(() => this.onLayersChanged()); });
			this.dataLifecycleHandler.discarded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { this.ps.clearProject().then(() => this.onLayersChanged()); });
		}
		this.ps.discarded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onLayersChanged());

		// Projekt-Wechsel: Zeichnungslayer laden/entladen
		let lastProject: string | null = null;
		effect(() => {
			const project = this.ps.project();
			// Nur bei echtem Projekt-Wechsel reagieren
			if (project !== lastProject) {
				lastProject = project;
				if (project) {
					this.drawingService.loadZeichnungslayer();
					// Im Offline-Modus: Layer neu erstellen damit projektspezifische Tiles geladen werden
					if (this.oms.offlineMode()) {
						this.ls.clearLayerCache();
						this.onLayersChanged();
					}
				} else {
					this.drawingService.clearZeichnungslayer();
					// Regions und Layer aktualisieren (Bereiche von Karte entfernen)
					this.ls.clearLayerCache();
					this.onLayersChanged();
				}
			}
		});

		// Wenn ein Zeichnungslayer aktiviert/deaktiviert wird — an DrawingService delegieren
		let previousLayerId: string | null = null;
		effect(() => {
			const activeLayerId = this.ls.activeDrawLayerId();
			this.drawingService.onActiveDrawLayerChanged(activeLayerId, previousLayerId);
			previousLayerId = activeLayerId;
		});

		// Selection changes
		this.selectionService.selectionChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onSelectionChanged());
	}

	onAuthChanged() {
		this.authMutex.lock().then((unlock) => {
			try {
				console.log('Auth change started');
				if (this.map) {
					this.initEditControls();
					this.onPointsChanged();
					// Punkt-Anlegen-Modus beenden wenn keine Berechtigung mehr
					if (this.drawingService.newPointOnMapActive && !this.drawingService.canCreateNewPoint()) {
						this.drawingService.endNewPointOnMapMode();
					}
				}
			} finally {
				console.log('Auth change handling done');
				unlock();
			}
		});
	}
	
	ngDoCheck(): void {
		if (!this.mapDiv) return;
		const newMapSize = this.mapDiv.nativeElement.getBoundingClientRect();
		if (!this.mapSize || newMapSize.height !== this.mapSize.height || newMapSize.width !== this.mapSize.width)
			this.invalidateMapSize = true;
		this.mapSize = newMapSize;
	}

	ngAfterViewChecked(): void {
		if (this.invalidateMapSize) {
			this.map?.invalidateSize();
			this.invalidateMapSize = false;
		}
	}

  // makeTileLayer → MapLayerFactoryService

	private _subscribeTo(o: Observable<any>, key: string, fn: (v: any) => void) {
		if (this._subscriptions.has(key)) {
			this._subscriptions.get(key)?.unsubscribe();
		}
		this._subscriptions.set(key, o.subscribe(fn));
	}

  // makeWMSTileLayers, _attachTileLoadingEvents, makeGeoPackageTileLayer,
  // makeGeoPackageLayers, makeGeoTiffRasterLayer, makeGeoJsonLayer → MapLayerFactoryService

  ngOnInit() {
		const doInit = () => {
			this.options = {
				zoomControl: false,
				zoom: this.mapStatus!.zoom,
				zoomSnap: 1,           // Ganze Zoomstufen für saubere Tile-Darstellung
				bounceAtZoomLimits: false,
				center: this.mapStatus!.center,
				layers: [],
				attributionControl: true,
				// maxBounds: mapLimits.maxSubBounds,
				// maxBoundsViscosity: 1.0,
				minZoom: mapLimits.minZoom,
				maxZoom: mapLimits.maxZoom
			};
			this._subscribeTo(this.os.seedRequest, "seedRequest", (plusLevels: 0|1|2) => {
				// Wenn Layer noch nicht geladen, gerade am Laden, oder Layer-Change ansteht, Request zwischenspeichern
				if (Object.keys(this.mapLayers).length === 0 || this.isLoadingLayers || this.layerChangeTimeout) {
					this.pendingSeedRequest = plusLevels;
				} else {
					this.os.seedAllLayers(plusLevels, this.mapLayers, this.mapStatus, () => {});
				}
			});
			this._subscribeTo(this.os.seedLayerRequest, "seedLayerRequest", (layerName: string) => {
				// Wenn Layer noch nicht geladen, gerade am Laden, oder Layer-Change ansteht, Request zwischenspeichern
				if (Object.keys(this.mapLayers).length === 0 || this.isLoadingLayers || this.layerChangeTimeout) {
					this.pendingSeedLayerRequest = layerName;
					console.log('Single layer seed request queued - waiting for layers to load:', layerName);
				} else {
					this.seedSingleLayer(layerName);
				}
			});
			this.ns.subscribe(Command.removeAttachment, (a: string) => this.onRemoveAttachment(a));
			this.ns.subscribe(Command.gpsHome, () => this.onGpsHome());

			this.onAuthChanged();
		};

		const mapLimits = this.mapStatusService.mapLimits;
		doInit();

		// Periodische Selbstprüfung: Prüft alle 60 Sekunden ob Layer fehlen
		setInterval(() => {
			this.checkLayerIntegrity();
		}, 60000);
  }

	/**
	 * Prüft ob alle erwarteten Layer auf der Map vorhanden sind.
	 * Bei fehlenden Layern wird ein Neu-Laden ausgelöst.
	 */
	private checkLayerIntegrity(): void {
		// Nicht prüfen wenn: Map nicht da, kein mapDef, gerade am Laden, oder Layer-Change ansteht
		if (!this.map || !this.ls.mapDef() || this.isLoadingLayers || this.scheduleLayerChange) return;
		// Auch nicht prüfen wenn ein Debounce-Timeout noch läuft
		if (this.layerChangeTimeout) return;

		const expectedLayers = this.ls.numberOfLayers();
		const actualLayers = Object.keys(this.mapLayers).length;

		// Wenn keine Layer erwartet werden, nichts tun
		if (expectedLayers === 0) return;

		// Wenn deutlich weniger Layer vorhanden sind als erwartet
		if (actualLayers < expectedLayers * 0.5) {
			console.warn(`Layer-Integritätsprüfung: Nur ${actualLayers} von ${expectedLayers} Layern vorhanden. Löse Neu-Laden aus.`);
			this.onLayersChanged();
		}
	}
	
	ngOnDestroy(): void {
		// Highlight aufräumen
		this.highlightMarkerCircle?.remove();
		// Services aufräumen
		this.drawingService.destroy();
		this.gpkgRenderingService.destroy();
		this.pointRenderingService.destroy();
		this.polygonRenderingService.destroy();
		this.mapLayerFactoryService.destroy();
		this.freehandRegionService.destroy();
		this.layerOrderingService.destroy();

		// Layer in Cache verschieben für schnelle Wiederherstellung
		Object.entries<Layer>(this.mapLayers).forEach(([name, l]: [string, Layer]) => {
			this.ls.cacheLayer(name, l);
		});

		this._subscriptions.forEach((s) => s.unsubscribe());
	}

  ngOnChanges(changes: SimpleChanges): void {
    // Nur bei echtem Wechsel des online-Status die Layer neu laden
    if (changes['online'] && changes['online'].previousValue !== changes['online'].currentValue
        && changes['online'].previousValue !== undefined) {
      this.onLayersChanged();
    }
    if (changes['draggablePoints']) {
      this.pointRenderingService.draggablePoints = this.draggablePoints;
      if (!changes['points']) this.onPointsChanged(); // Marker neu erstellen mit draggable-Option
    }
    if (changes['points']) this.onPointsChanged();
    if (changes['pointsFilter']) this.onFilterChanged();
    if (changes['polygons']) {
      this.onPolygonsChanged();
    } else if (changes['highlightPolygonIds']) {
      if (this.map && this.polygonRenderingService.polygonsLayer) {
        this.polygonRenderingService.updateHighlighting();
      } else {
        this.onPolygonsChanged();
      }
    }
    if (changes['highlightMarkerId']) this.updateHighlightMarker();
    if (changes['draggablePoint']) this.onDraggablePointChanged();
    // Wenn die Karte sichtbar wird, invalidateSize aufrufen damit Tiles geladen werden
    if (changes['visible'] && changes['visible'].currentValue === true) {
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 50);
    }
  }

	// #region Punkt-Methoden (App-spezifisch)

	private onFilterChanged() {
		this.onPointsChanged();
		// updateScaling() nicht mehr nötig - Icons werden in createMarkersWithLabel() bereits korrekt erstellt
	}

  // createMarkersWithLabel → PointRenderingService

	// Projekt-Zuweisungslogik wurde in ProjectAssignmentHandler ausgelagert

	// --- Kontextmenü-Methoden ---

	/**
	 * Öffnet das Kontextmenü für einen Punkt.
	 */
	openContextMenu(point: MapMarker): void {
		this.lastClickedPointId = point.id;
		this.contextMenuMarker = point;
		this.pointClicked.emit(point);
		if (point.latLng && this.map) {
			const latlng = point.latLng;
			// Position des Punkts auf dem Bildschirm berechnen
			const containerPoint = this.map.latLngToContainerPoint(latlng);
			const mapRect = this.map.getContainer().getBoundingClientRect();
			const pointX = mapRect.left + containerPoint.x;
			const pointY = mapRect.top + containerPoint.y;

			// Menü-Größe schätzen (ca. 220px breit, 300px hoch)
			const menuWidth = 220;
			const menuHeight = 300;
			const margin = 40; // Größerer Abstand zum Punkt

			// Bevorzugt links vom Punkt positionieren, falls Platz
			let x: number;
			if (pointX - margin - menuWidth > 10) {
				x = pointX - margin - menuWidth; // Links vom Punkt
			} else {
				x = pointX + margin; // Rechts vom Punkt (Fallback)
			}

			// Y-Position: vertikal zentriert am Punkt, aber im sichtbaren Bereich
			let y = pointY;
			const halfMenu = menuHeight / 2;
			if (y - halfMenu < 10) {
				y = halfMenu + 10;
			} else if (y + halfMenu > window.innerHeight - 10) {
				y = window.innerHeight - halfMenu - 10;
			}

			this.contextMenuPosition = { x, y };

			// Roten Highlight-Circle um den Punkt anzeigen
			this.contextMenuHighlight?.remove();
			const scale = Math.pow(2, this.map.getZoom() - 13);
			const radius = Math.max(12, Math.min(20, scale * 10));
			this.contextMenuHighlight = L.circleMarker(latlng, {
				radius: radius,
				color: '#dc3545',
				weight: 3,
				fillOpacity: 0,
				pane: 'markerPane' // Sicherstellen dass es über den Punkten liegt
			}).addTo(this.map);
		}
	}

	/**
	 * Schließt das Kontextmenü.
	 */
	closeContextMenu(): void {
		this.contextMenuMarker = null;
		this.contextMenuPosition = null;
		this.contextMenuHighlight?.remove();
		this.contextMenuHighlight = undefined;
	}

	/**
	 * Formatiert ein Datum für die Anzeige im Kontextmenü.
	 */
	formatDate(date: string | Date | undefined): string {
		if (!date) return '';
		const d = new Date(date);
		if (isNaN(d.getTime())) return '';
		return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
	}

	/**
	 * Prüft ob der Punkt dem aktiven Projekt zugeordnet werden kann.
	 */
	canAssignToProject(): boolean {
		if (!this.contextMenuMarker || !this.projectHandler) return false;
		if (!this.projectHandler.canAssign()) return false;
		// Bereits zugeordnet?
		return !this.pointRenderingService.assignedPointIds.has(this.contextMenuMarker.id);
	}

	/**
	 * Prüft ob der Punkt aus dem aktiven Projekt entfernt werden kann.
	 */
	canRemoveFromProject(): boolean {
		if (!this.contextMenuMarker || !this.projectHandler) return false;
		return this.projectHandler.canRemove(this.contextMenuMarker.id);
	}

	/**
	 * Prüft ob der Punkt gelöscht werden kann.
	 */
	canDeletePoint(): boolean {
		return this.aus.canI('add', 'points');
	}

	/**
	 * Kontextmenü: Punkt öffnen.
	 */
	onContextMenuOpen(): void {
		if (!this.contextMenuMarker) return;
		const id = this.contextMenuMarker.id;
		this.closeContextMenu();
		this.navigationHandler?.navigateToDetail(id);
	}

	/**
	 * Kontextmenü: Auswahl umschalten.
	 */
	onContextMenuToggleSelect(): void {
		if (!this.contextMenuMarker) return;
		this.selectionService.toggle(this.contextMenuMarker.id);
		this.closeContextMenu();
	}

	/**
	 * Kontextmenü: Punkt dem Projekt zuordnen.
	 */
	async onContextMenuAssignToProject(): Promise<void> {
		if (!this.contextMenuMarker || !this.projectHandler) return;
		const result = await this.projectHandler.assignToProject(this.contextMenuMarker.id);
		if (result.success) {
			this.pointRenderingService.assignedPointIds.add(this.contextMenuMarker.id);
			this.onPointsChanged();
		}
		this.closeContextMenu();
	}

	/**
	 * Kontextmenü: Punkt aus Projekt entfernen.
	 */
	async onContextMenuRemoveFromProject(): Promise<void> {
		if (!this.contextMenuMarker || !this.projectHandler) return;
		const result = await this.projectHandler.removeFromProject(this.contextMenuMarker.id);
		if (result.success) {
			this.pointRenderingService.assignedPointIds.delete(this.contextMenuMarker.id);
			this.onPointsChanged();
		}
		this.closeContextMenu();
	}

	/**
	 * Kontextmenü: Punkt löschen.
	 */
	async onContextMenuDelete(): Promise<void> {
		if (!this.contextMenuMarker || !this.projectHandler) return;
		const marker = this.contextMenuMarker;
		// Kontextmenü schließen bevor Dialog öffnet, damit Dialog im Vordergrund ist
		this.closeContextMenu();
		const confirmed = await this.confirmService.confirmDelete(
			`Soll der Punkt '${marker.label || marker.id}' wirklich entfernt werden?`
		);
		if (confirmed) {
			const result = await this.projectHandler.deletePoint(marker.id);
			if (result.success) {
				this.pointDeleted.emit();
			}
		}
	}
  // Punkt-Rendering-Methoden → PointRenderingService (Plan 15 Phase 3b)

  private onPointsChanged() {
    if (!this.points) return;
    if (!this.map) {
      this.schedulePointsChange = true;
      return;
    }
    this.schedulePointsChange = false;
    this.pointRenderingService.onPointsChanged();
  }

  // Polygon-Rendering-Methoden → PolygonRenderingService

  private onPolygonsChanged() {
    if (!this.polygons) return;
    if (!this.map) {
      this.schedulePolygonsChange = true;
      return;
    }
    this.schedulePolygonsChange = false;
    this.polygonRenderingService.onPolygonsChanged();

    // Flächen-Sichtbarkeit aus Projekteinstellungen anwenden
    this.ps.getProjectValue('polygonsLayerVisible').then((visible: unknown) => {
      if ((visible as boolean | null) === false) {
        this.setPolygonsLayerVisibility(false);
      }
    });
  }

	// --- Polygon-Kontextmenü ---

	openPolygonContextMenu(polygon: MapPolygon): void {
		// Erster Klick: nur highlighten und Event emittieren
		// Zweiter Klick auf dasselbe Polygon: Kontextmenü öffnen
		if (this.lastClickedPolygonId !== polygon.id) {
			this.lastClickedPolygonId = polygon.id;
			this.closePolygonContextMenu();
			this.polygonClicked.emit(polygon);
			return;
		}

		this.contextMenuPolygon = polygon;
		this.polygonClicked.emit(polygon);
		// Position: Mitte des ersten Polygons
		const firstRing = polygon.polygons[0];
		if (firstRing && firstRing.length > 0 && this.map) {
			const bounds = L.latLngBounds(firstRing);
			const center = bounds.getCenter();
			const containerPoint = this.map.latLngToContainerPoint(center);
			const mapRect = this.map.getContainer().getBoundingClientRect();
			const pointX = mapRect.left + containerPoint.x;
			const pointY = mapRect.top + containerPoint.y;

			const menuWidth = 200;
			let x: number;
			if (pointX - 40 - menuWidth > 10) {
				x = pointX - 40 - menuWidth;
			} else {
				x = pointX + 40;
			}
			this.contextMenuPolygonPosition = { x, y: pointY };

			// Highlight
			this.contextMenuPolygonHighlight?.forEach(p => p.remove());
			this.contextMenuPolygonHighlight = polygon.polygons.map(ring =>
				L.polygon(ring, {
					color: '#dc3545',
					weight: 3,
					fillOpacity: 0,
					interactive: false,
					pane: 'markerPane'
				}).addTo(this.map)
			);
		}
	}

	closePolygonContextMenu(): void {
		this.contextMenuPolygon = null;
		this.contextMenuPolygonPosition = null;
		this.contextMenuPolygonHighlight?.forEach(p => p.remove());
		this.contextMenuPolygonHighlight = undefined;
	}

	onPolygonContextMenuOpen(): void {
		if (!this.contextMenuPolygon) return;
		const id = this.contextMenuPolygon.id;
		this.closePolygonContextMenu();
		this.navigationHandler?.navigateToPolygonDetail?.(id);
	}

	// --- Marker-Highlight (einzelner Punkt, z.B. aus Baum-Selektion) ---

	private updateHighlightMarker(): void {
		// Altes Highlight entfernen
		if (this.highlightMarkerCircle) {
			this.highlightMarkerCircle.remove();
			this.highlightMarkerCircle = undefined;
		}
		if (!this.highlightMarkerId || !this.map || !this.points) return;

		const point = this.points.find(p => p.id === this.highlightMarkerId);
		if (!point) return;

		const scale = Math.pow(2, (this.map.getZoom() || 13) - 13);
		this.highlightMarkerCircle = L.circleMarker(point.latLng, {
			radius: Math.max(14, Math.min(22, scale * 12)),
			color: '#FF8C00',
			weight: 3,
			fillOpacity: 0,
			interactive: false,
			pane: 'markerPane'
		}).addTo(this.map);
	}

	onPolygonContextMenuHighlight(): void {
		// Highlight bleibt bestehen, Menü wird geschlossen
		const polygon = this.contextMenuPolygon;
		this.contextMenuPolygon = null;
		this.contextMenuPolygonPosition = null;
		// Highlight bleibt stehen — wird beim nächsten polygonsChanged aktualisiert
	}

  private static draggableIcon = new Icon.Default({
    imagePath: 'assets/leaflet/'
  });

  private onDraggablePointChanged() {
    if (!this.draggablePoint) return;
    if (!this.map) {
      this.scheduleDraggablePointChange = true;
      return;
    }
    this.scheduleDraggablePointChange = false;
    this.draggablePointMarker = new L.Marker(this.draggablePoint, {
      draggable: true,
      icon: MapComponent.draggableIcon,
      zIndexOffset: 1000
    });
    this.draggablePointMarker.addEventListener("dragend", (event: L.DragEndEvent) => this.onPointDragEnd());
		const ll = this.draggablePointMarker.getLatLng();
		if (!this.map.getBounds().contains(ll))
			this.map.panTo(ll);

    if (this.pointLayer)
      this.pointLayer.removeFrom(this.map);
    this.pointLayer = new LayerGroup([this.draggablePointMarker]);
    this.pointLayer.addTo(this.map);
  }

  onRepositionConfirm() {
    if (this.map) {
      this.repositionConfirmed.emit(this.map.getCenter());
    }
  }

  private onPointDragEnd() {
    if (this.draggablePoint) {
      this.draggablePoint = this.draggablePointMarker?.getLatLng();
      this.draggablePointChange.emit(this.draggablePoint);
    }
  }

  /**
   * Prüft ob ein sichtbarer Punkt unter der Klick-Position liegt und öffnet das Kontextmenü.
   * Wird als prioritärer Klick-Handler in der Capture-Phase verwendet, damit Punkte immer Vorrang haben.
   */
  private handlePriorityPointClick(e: MouseEvent): void {
    if (!this.pointRenderingService.pointsLayer || !this.map) return;

    // Im eingeschränkten Modus kein Punkt-Kontextmenü
    if (this.restrictedMode) return;

    // Nicht aktiv wenn die Karte gerade gepannt wurde
    if (this.mapWasDragged) return;

    // Nicht aktiv wenn Punkte-Layer versteckt ist
    if (!this.map.hasLayer(this.pointRenderingService.pointsLayer)) return;

    // Nicht aktiv wenn ein Zeichenmodus oder anderes Tool aktiv ist
    if (this.drawingService.isAnyDrawModeActive()) return;
    if (this.drawingService.newPointOnMapActive) return;
    if (this.wmsInfoService.wmsInfoActive) return;
    if (this.mapSelectionService.selectionMode !== 'none') return;

    // Klick-Position relativ zum Map-Container berechnen
    const container = this.map.getContainer();
    const rect = container.getBoundingClientRect();
    const clickPoint = L.point(e.clientX - rect.left, e.clientY - rect.top);
    const clickRadius = 20; // Pixel-Toleranz für Klick

    // Durch alle Marker iterieren und prüfen ob sie sichtbar sind (nicht in Cluster)
    const allMarkers = this.pointRenderingService.pointsLayer.getLayers() as L.Marker[];

    for (const marker of allMarkers) {
      // Prüfen ob Marker ein Cluster ist
      if ((marker as any).getChildCount) continue;

      const markerLatLng = marker.getLatLng();
      if (!markerLatLng) continue;

      // Prüfen ob Marker sichtbar ist (nicht in einem Cluster versteckt)
      const visibleParent = (this.pointRenderingService.pointsLayer as any).getVisibleParent?.(marker);
      if (visibleParent && visibleParent !== marker) continue; // Marker ist in Cluster

      const markerPoint = this.map.latLngToContainerPoint(markerLatLng);
      const distance = clickPoint.distanceTo(markerPoint);

      if (distance <= clickRadius) {
        // Punkt gefunden - Kontextmenü öffnen und Event stoppen
        e.stopPropagation();
        e.preventDefault();

        // BP-ID aus Marker-Options holen und zugehöriges BP-Objekt finden
        const bpId = (marker.options as any).bpId;
        if (bpId && this.points) {
          const point = this.points.find(p => p.id === bpId);
          if (point) {
            this.openContextMenu(point);
          }
        }
        return;
      }
    }
  }


	// createGridPoints, updatePointGrid, metersToPixels → PointRenderingService

	private _addLayerToMap(name: string, l: Layer, onTop: boolean) {
		// Prüfen ob Map vollständig initialisiert ist
		if (!this.map || !this.map.getPane('mapPane')) {
			this.schedulePointsChange = true;
			return;
		}

		// Vorhandenen Layer sicher entfernen
		if (this.mapLayers[name]) {
			try {
				this.mapLayers[name].removeFrom(this.map);
			} catch (e) {
				// Layer war möglicherweise nicht korrekt hinzugefügt
				console.warn(`Fehler beim Entfernen von Layer "${name}":`, e);
			}
		}

		this.mapLayers[name] = l;

		try {
			// Zeichnungslayer haben bereits ihre eigenen Panes
			if (name === 'Zeichnungen_Linien' || name === 'Zeichnungen_Polygone') {
				// Pane wurde bereits in loadZeichnungslayer erstellt
				l.addTo(this.map);
				return;
			}

			// Custom Pane für diesen Layer erstellen (falls noch nicht vorhanden)
			const paneName = `layer-${name.replace(/[^a-zA-Z0-9]/g, '_')}`;
			if (!this.map.getPane(paneName)) {
				const pane = this.map.createPane(paneName);
				// Standard-z-Index setzen (wird von onLayerOrderChanged überschrieben)
				// onTop: höherer z-Index für Overlay-Layer wie Punktgitter
				pane.style.zIndex = onTop ? '600' : '450';
			}

			// Pane VOR addTo setzen (wichtig für TileLayer wie WMS)
			// Bei TileLayern muss das Pane vor dem ersten addTo gesetzt sein
			(l.options as any).pane = paneName;

			// Bei TileLayern auch _pane direkt setzen falls Container schon existiert
			const tl = l as any;
			if (tl._container) {
				// Layer war schon mal auf der Map - Container in neues Pane verschieben
				const pane = this.map.getPane(paneName);
				if (pane && tl._container.parentNode !== pane) {
					pane.appendChild(tl._container);
				}
			}

			l.addTo(this.map);
		} catch (e) {
			// Map ist möglicherweise nicht vollständig initialisiert
			console.warn(`Fehler beim Hinzufügen von Layer "${name}":`, e);
			this.schedulePointsChange = true;
		}
	}
	
	onLayerVisibilityChanged() {
		if (!this.ps.project()) return;
		this.ps.getProjectValue('hiddenLayers').then((result: unknown) => {
			const hiddenLayers = result as string[] | undefined;
			const resolvedSublayerNames: string[] = [];

			// Versteckte GPKG-Layer für Click-Detection aktualisieren
			this.gpkgRenderingService.hiddenGpkgLayers.clear();
			hiddenLayers?.forEach((hl: string) => {
				if (!hl) return;
				// GPKG-Sublayer-Key (Format: "dateiname.gpkg_sublayername")
				if (/^.+\.gpkg_.+$/i.test(hl)) {
					this.gpkgRenderingService.hiddenGpkgLayers.add(hl);
				}
			});

			hiddenLayers?.forEach((hl: string) => {
				if (!hl) return;
				// Prüfen ob es ein GPKG-Sublayer-Key ist (Format: "dateiname.gpkg_sublayername")
				const gpkgMatch = hl.match(/^(.+\.gpkg)_(.+)$/i);
				if (gpkgMatch) {
					// GPKG-Sublayer: Qualifizierten Namen verwenden (z.B. "fluerstueck.gpkg_fluerstueck")
					resolvedSublayerNames.push(hl);
					return;
				}

				// WMS-Sublayer-Key: "WmsServerName_SublayerTitle"
				const mapDefLayers = this.ls.mapDef()?.layers;
				const wmsLd = mapDefLayers?.find((layer: LayerDetails) =>
					layer.type === 'wms' && hl.startsWith(layer.name + '_')
				);
				if (wmsLd) {
					const sublayerTitle = hl.substring(wmsLd.name.length + 1);
					resolvedSublayerNames.push(sublayerTitle);
					return;
				}

				const ld = mapDefLayers?.find((layer: LayerDetails) => layer.name === hl);
				// WMS und gpkg haben Sublayers die aufgelöst werden müssen
				if (ld?.type === 'wms') {
					// Rückwärtskompatibel: Parent-Name versteckt alle Sublayer
					ld.layers?.forEach((ol: OptionalLayer) => resolvedSublayerNames.push(ol.title));
				} else if (ld?.type === 'gpkg') {
					ld.layers?.forEach((ol: OptionalLayer) => resolvedSublayerNames.push(`${ld.name}_${ol.name}`));
				} else {
					resolvedSublayerNames.push(hl);
				}
			});

			const setLayerVisibility = (layer: any, name: string) => {
				const shouldHide = resolvedSublayerNames.includes(name);
				const set = (c: any) => {
					c.style.display = shouldHide ? 'none' : '';
				}
				if (layer._container) set(layer._container);
				if (layer._path) set(layer._path);
				if (layer._icon) set(layer._icon);

				// Canvas-Renderer: Pane direkt steuern (für GPKG-Layer)
				const paneName = `layer-${name.replace(/[^a-zA-Z0-9]/g, '_')}`;
				const pane = this.map?.getPane(paneName);
				if (pane) {
					pane.style.display = shouldHide ? 'none' : '';
				}
			};
			const iterateLayerVisibility = (layer: any, name: string) => {
				setLayerVisibility(layer as any, name);
				if (layer._layers) {
					Object.values(layer._layers).forEach((element: any) => {
						iterateLayerVisibility(element, name);
					});
				}
			};
			Object.entries<Layer>(this.mapLayers).forEach(([name, layer]: [string, Layer]) => {
				iterateLayerVisibility(layer as any, name);
			});

			// GPKG-Label- und Haken-Layer Sichtbarkeit steuern
			this.gpkgRenderingService.updateLabelLayerVisibility(resolvedSublayerNames);
		});
	}

	// onLayerOrderChanged, setPointsLayerZIndex, setLayerZIndex → LayerOrderingService

  onLayersChanged() {
    // Warten bis Map vollständig initialisiert ist (inkl. mapPane)
    if (!this.map || !this.map.getPane('mapPane') || !this.ls.mapDef()) {
      this.scheduleLayerChange = true;
      return;
    }

    // Wenn bereits am Laden, für später merken und nicht blockieren
    if (this.isLoadingLayers) {
      this.scheduleLayerChange = true;
      return;
    }

    // Debounce: Mehrfache schnelle Aufrufe zusammenfassen
    if (this.layerChangeTimeout) {
      clearTimeout(this.layerChangeTimeout);
    }
    this.layerChangeTimeout = setTimeout(() => {
      this.layerChangeTimeout = null;
      this._doLayersChanged();
    }, 200);
  }

  private async _doLayersChanged() {
		this.scheduleLayerChange = false;
		this.isLoadingLayers = true;

		const hiddenSet = await this._resolveHiddenLayers();
		this._removeOldLayers();

    this.layers = this.ls.mapDef().layers;
		const createdLayers = new Map<string, Layer>();
		const indexMap = new Map<string, number>();

		const { baseLayers, transparentOverlayLayers, opaqueOverlayLayers } = this._categorizeMapLayers();

		const expectedCount = this.ls.numberOfLayers();
		let callbackCount = 0;
		let allLayersDone = false;
		const maybeDone = (name: string, l: Layer) => {
			if (allLayersDone) return;
			callbackCount++;
			createdLayers.set(name, l);
			if (l) {
				this._addCreatedLayerToMap(name, l, indexMap);
			}
			if (callbackCount >= expectedCount) {
				allLayersDone = true;
				this._onAllLayersCreated(createdLayers);
			}
		};

		this._createAllLayers(baseLayers, opaqueOverlayLayers, transparentOverlayLayers, hiddenSet, maybeDone);
		this._addSystemLayers();
		this._startLoadingTimeout();
  }

	/**
	 * Versteckte Layer laden und zu Sublayer-Namen auflösen
	 */
	private async _resolveHiddenLayers(): Promise<Set<string>> {
		const hiddenLayers = await this.ls.hiddenLayers();
		const hiddenSet = new Set<string>();
		const mapDefLayers = this.ls.mapDef()?.layers;
		hiddenLayers.forEach((hl: string) => {
			if (!hl) return;
			const gpkgMatch = hl.match(/^(.+\.gpkg)_(.+)$/i);
			if (gpkgMatch) {
				hiddenSet.add(hl);
				return;
			}
			// WMS-Sublayer-Key: "WmsServerName_SublayerTitle"
			const wmsLd = mapDefLayers?.find((layer: LayerDetails) =>
				layer.type === 'wms' && hl.startsWith(layer.name + '_')
			);
			if (wmsLd) {
				const sublayerTitle = hl.substring(wmsLd.name.length + 1);
				hiddenSet.add(sublayerTitle);
				return;
			}
			const ld = mapDefLayers?.find((layer: LayerDetails) => layer.name === hl);
			if (ld?.type === 'wms') {
				// Rückwärtskompatibel: Parent-Name versteckt alle Sublayer
				ld.layers?.forEach((ol: OptionalLayer) => hiddenSet.add(ol.title));
			} else if (ld?.type === 'gpkg') {
				ld.layers?.forEach((ol: OptionalLayer) => hiddenSet.add(`${ld.name}_${ol.name}`));
			} else {
				hiddenSet.add(hl);
			}
		});
		return hiddenSet;
	}

	/**
	 * Bestehende Layer von der Map entfernen, in Cache verschieben
	 */
	private _removeOldLayers() {
		this.mapStatusService.tilesLoading.set(0);
		Object.entries<Layer>(this.mapLayers).forEach(([name, l]: [string, Layer]) => {
			l.off('loading');
			l.off('load');
			this.ls.cacheLayer(name, l);
			l.remove();
		});
		this.mapLayers = {};
		this.gpkgRenderingService.cleanupAllGpkgLayers();
	}

	/**
	 * Layer-Definitionen in Base, opaque Overlays und transparente Overlays einteilen
	 */
	private _categorizeMapLayers() {
		const baseLayers: LayerDetails[] = [];
		const transparentOverlayLayers: LayerDetails[] = [];
		const opaqueOverlayLayers: LayerDetails[] = [];
		this.layers?.forEach(ld => {
			if (ld.type == "osm" || ld.isBase) baseLayers.push(ld);
			else {
				const transparent = this.ls.isFullyTransparent(ld);
				if (ld.transparent && transparent)
					transparentOverlayLayers.push(ld);
				else
					opaqueOverlayLayers.push(ld);
			}
		});
		return { baseLayers, transparentOverlayLayers, opaqueOverlayLayers };
	}

	/**
	 * Einen erstellten Layer zur Map hinzufügen und Index speichern
	 */
	private _addCreatedLayerToMap(name: string, l: Layer, indexMap: Map<string, number>): boolean {
		let layerIdx = 0;
		let isOverlay = true;
		this.layers?.forEach((ld: LayerDetails, idx: number) => {
			if (ld.layers && ld.layers.length > 0) {
				const opt = ld.layers.find((o: OptionalLayer) =>
					ld.type === 'gpkg' ? name === `${ld.name}_${o.name}` : o.title === name
				);
				if (opt) {
					layerIdx = idx;
					isOverlay = !ld.isBase;
				}
			} else if (ld.name === name) {
				layerIdx = idx;
				isOverlay = !ld.isBase;
			}
		});
		this._addLayerToMap(name, l, isOverlay);
		indexMap.set(name, layerIdx);
		return isOverlay;
	}

	/**
	 * Finalisierung wenn alle Layer erstellt wurden: Visibility, Bounds, Order, Seed-Requests
	 */
	private _onAllLayersCreated(createdLayers: Map<string, Layer>) {
		this.onLayerVisibilityChanged();

		// Bounds für Geodaten-Layer berechnen (falls noch nicht vorhanden)
		const geodataTypes = new Set(['geotiff', 'gpkg', 'geojson']);
		let mapDefUpdated = false;
		this.layers?.forEach((ld: LayerDetails) => {
			if (!geodataTypes.has(ld.type) || ld.bbox) return;

			if (ld.type === 'gpkg' && ld.layers && ld.layers.length > 0) {
				let combinedBounds: LatLngBounds | undefined;
				ld.layers.forEach((opt: OptionalLayer) => {
					if (!opt.enabled) return;
					const fullName = `${ld.name}_${opt.name}`;
					const layer = createdLayers.get(fullName);
					const bounds = this._getLayerBounds(layer);
					if (bounds) {
						combinedBounds = combinedBounds ? combinedBounds.extend(bounds) : bounds;
					}
				});
				if (combinedBounds?.isValid()) {
					ld.bbox = combinedBounds;
					mapDefUpdated = true;
				}
			} else {
				const layer = createdLayers.get(ld.name);
				const bounds = this._getLayerBounds(layer);
				if (bounds?.isValid()) {
					ld.bbox = bounds;
					mapDefUpdated = true;
				}
			}
		});
		if (mapDefUpdated) {
			this.ls.saveLayerOrderOnly(this.ls.mapDef()!);
		}
		this.layerOrderingService.onLayerOrderChanged();

		// Ausstehende Seed-Requests ausführen
		if (this.pendingSeedRequest !== null) {
			const plusLevels = this.pendingSeedRequest;
			this.pendingSeedRequest = null;
			this.os.seedAllLayers(plusLevels, this.mapLayers, this.mapStatus, () => {});
		}
		if (this.pendingSeedLayerRequest !== null) {
			const layerName = this.pendingSeedLayerRequest;
			this.pendingSeedLayerRequest = null;
			this.seedSingleLayer(layerName);
		}

		this.isLoadingLayers = false;

		if (this.scheduleLayerChange) {
			setTimeout(() => this.onLayersChanged(), 0);
		}
	}

	/**
	 * Layer nach Typ erstellen und zur Map hinzufügen
	 */
	private _createAllLayers(
		baseLayers: LayerDetails[],
		opaqueOverlayLayers: LayerDetails[],
		transparentOverlayLayers: LayerDetails[],
		hiddenSet: Set<string>,
		maybeDone: (name: string, l: Layer) => void
	) {
		const createLayer = (ld: LayerDetails) => {
			const tileFunc = (l: L.Layer, layerName?: string) => {
				const name = layerName || (l?.options as any)?.name;
				maybeDone(name, l);
			};
			const func = (l: L.Layer, name?: string) => {
				const resolvedName = name || (l?.options as any)?.layerName;
				maybeDone(resolvedName, l);
			};
			switch (ld.type) {
				case "wms": {
					let layerStr = '';
					ld.layers?.forEach((l: OptionalLayer) => { if (l.enabled) layerStr += layerStr.length===0 ? l.name : `,${l.name}`});
					this.mapLayerFactoryService.makeWMSTileLayers(ld, { layers: layerStr, format: 'image/png', transparent: ld.transparent, maxZoom: ld.maxZoom, bounds: this.mapStatusService.mapLimits.maxSubBounds, noWrap: true }, tileFunc, hiddenSet);
					break;
				}
				case "osm": {
					if (ld.display) {
						this.mapLayerFactoryService.makeTileLayer(ld.url, { maxZoom: ld.maxZoom, bounds: this.mapStatusService.mapLimits.maxGlobalBounds, noWrap: false }, ld.name, tileFunc);
					}
					break;
				}
				case "gpkg": {
					this.mapLayerFactoryService.makeGeoPackageLayers(ld, func, (name) => maybeDone(name, null as any));
					break;
				}
				case "geotiff": {
					this.mapLayerFactoryService.makeGeoTiffRasterLayer(ld.url, ld.resolution).then(l => {
						maybeDone(ld.name, l as Layer);
					});
					break;
				}
				case "grid": {
					if (ld.display) {
						this.pointRenderingService.gridLayer = { ld: ld, l: this.pointRenderingService.createGridPoints(ld) };
						maybeDone(this.pointRenderingService.gridLayer.ld.name, this.pointRenderingService.gridLayer.l);
					}
					break;
				}
				case "geojson": {
					this.mapLayerFactoryService.makeGeoJsonLayer(ld.url, (ld as any).geojsonStyle).then(l => {
						maybeDone(ld.name, l as Layer);
					});
					break;
				}
			}
		};
		baseLayers.forEach(createLayer);
		opaqueOverlayLayers.forEach(createLayer);
		transparentOverlayLayers.forEach(createLayer);
		this.onPointsChanged();
	}

	/**
	 * Zeichnungs-, Region- und GPS-Layer zur Map hinzufügen
	 */
	private _addSystemLayers() {
		if (this.drawingService.zeichnungenLinien) {
			this._addLayerToMap('Zeichnungen_Linien', this.drawingService.zeichnungenLinien, true);
		}
		if (this.drawingService.zeichnungenPolygone) {
			this._addLayerToMap('Zeichnungen_Polygone', this.drawingService.zeichnungenPolygone, true);
		}

		if (!this.regions)
			this.regions = L.featureGroup();
		this._addLayerToMap('Projektbereiche', this.regions, true);
		this._addLayerToMap('Aktuelle GPS-Position', this.currentPosition, true);

		this.onRegionsChanged();

		if (this.ls.numberOfLayers() === 0) {
			this.isLoadingLayers = false;
		}
	}

	/**
	 * Timeout-Fallback: Wenn nach 15s nicht alle Layer geladen, trotzdem anzeigen
	 */
	private _startLoadingTimeout() {
		setTimeout(() => {
			if (this.isLoadingLayers) {
				console.warn(`Layer-Laden dauert zu lange (>15s) - zeige vorhandene Layer an.`, Object.keys(this.mapLayers));
				this.isLoadingLayers = false;
				this.onLayerVisibilityChanged();
				this.layerOrderingService.onLayerOrderChanged();
				if (this.scheduleLayerChange) {
					this.onLayersChanged();
				}
			}
		}, 15000);
	}

	private _updateProjectArea() {
		if (!this.map) return;

		// Entferne altes Overlay (falls vorhanden)
		const existingLayer = this.mapLayers["Projektbereich"];
		if (existingLayer) {
			existingLayer.removeFrom(this.map);
			delete this.mapLayers["Projektbereich"];
		}
		// Dunkles Overlay außerhalb des Projektbereichs deaktiviert (verursachte Flackern beim Zoomen)
	}

	private _calculateProjectAreaBounds(): LatLngBounds | null {
		let combined: LatLngBounds | null = null;

		// Regions einbeziehen
		const regions = this.ls.regions()?.regions ?? [];
		regions.forEach(region => {
			if (!combined) {
				combined = new LatLngBounds(region.bounds.getSouthWest(), region.bounds.getNorthEast());
			} else {
				combined.extend(region.bounds);
			}
		});

		// ProjectPoints einbeziehen
		if (this.pointRenderingService.projectPointIds && this.pointRenderingService.projectPointIds.size > 0 && this.points) {
			this.points.forEach(point => {
				if (this.pointRenderingService.projectPointIds.has(point.id) && point.latLng) {
					if (!combined) {
						combined = new LatLngBounds(point.latLng, point.latLng);
					} else {
						combined.extend(point.latLng);
					}
				}
			});
		}

		return combined;
	}

	hideProjectArea() {
		const layer = this.mapLayers["Projektbereich"];
		if (layer) {
			layer.removeFrom(this.map);
		}
	}

	showProjectArea() {
		this._updateProjectArea();
	}

  onMapReady(map: LMap) {
    this.map = map;
    this.map$.emit(this.map);

		// MapSelectionService initialisieren mit Bridge
		const selectionBridge: SelectionBridge = {
			getPoints: () => this.points,
			onSelectionChanged: () => this.onSelectionChanged()
		};
		this.mapSelectionService.init(map, selectionBridge, this.boxControl, this.selectionControl);

		// PointRenderingService initialisieren mit Bridge
		const pointBridge: PointRenderingBridge = {
			getPoints: () => this.points,
			getPointsFilter: () => this.pointsFilter,
			canReadPoints: () => this.aus.canI('read', 'points'),
			isOfflineMode: () => this.oms.offlineMode(),
			wasMapDragged: () => this.mapWasDragged,
			addLayerToMap: (name, layer, active) => this._addLayerToMap(name, layer, active),
			openContextMenu: (point) => this.openContextMenu(point),
			setPointsLayerVisibility: (visible) => this.setPointsLayerVisibility(visible),
			getProjectValue: (key) => this.ps.getProjectValue(key),
			onPointDragged: (pointId, newLatLng, revert) => this.pointDragged.emit({ id: pointId, latLng: newLatLng, revert })
		};
		this.pointRenderingService.draggablePoints = this.draggablePoints;
		this.pointRenderingService.init(map, pointBridge);

		// PolygonRenderingService initialisieren mit Bridge
		const polygonBridge: PolygonRenderingBridge = {
			getPolygons: () => this.polygons,
			getHighlightPolygonIds: () => this.highlightPolygonIds,
			wasMapDragged: () => this.mapWasDragged,
			addLayerToMap: (name, layer, active) => this._addLayerToMap(name, layer, active),
			openPolygonContextMenu: (polygon) => this.openPolygonContextMenu(polygon),
			isInteractive: () => true,
		};
		this.polygonRenderingService.init(map, polygonBridge);

		// GpkgRenderingService initialisieren mit Bridge
		const gpkgBridge: GpkgRenderingBridge = {
			getPoints: () => this.points,
			getPointsLayer: () => this.pointRenderingService.pointsLayer,
			isPointsLayerVisible: () => !!(this.pointRenderingService.pointsLayer && this.map?.hasLayer(this.pointRenderingService.pointsLayer)),
			getPointLabelMinZoom: () => this.pointRenderingService.POINT_LABEL_MIN_ZOOM
		};
		this.gpkgRenderingService.init(map, gpkgBridge);

		// DrawingService initialisieren mit Bridge
		const bridge: DrawingBridge = {
			addLayerToMap: (name, layer, active) => this._addLayerToMap(name, layer, active),
			getCurrentPositionLatLng: () => this.currentPosition.getLatLng()
		};
		this.drawingService.init(map, bridge);

		// MapLayerFactoryService initialisieren mit Bridge
		const layerFactoryBridge: LayerFactoryBridge = {
			getMapStatus: () => this.mapStatus
		};
		this.mapLayerFactoryService.init(map, layerFactoryBridge);
		this.freehandRegionService.init(map);
		this.layerOrderingService.init(map);

		// Sicherstellen, dass Dragging aktiviert ist
		this.map.dragging.enable();

		// Map-Dragging tracken (verhindert Kontextmenü bei Drag über Punkte)
		// mousedown setzt Flag zurück, dragstart setzt Flag
		const container = this.map.getContainer();
		container.addEventListener('mousedown', () => this.mapWasDragged = false, true);
		container.addEventListener('touchstart', () => this.mapWasDragged = false, { capture: true, passive: true });
		this.map.on('dragstart', () => this.mapWasDragged = true);
		this.map.on('drag', () => this.mapWasDragged = true);

		// Map-Click: Polygon-Erkennung wenn Polygone non-interactive sind (draggablePoints)
		this.map.on('click', (e: L.LeafletMouseEvent) => {
			if (!this.draggablePoints || this.mapWasDragged || !this.polygons) return;
			// Nicht feuern wenn der Klick bereits von einem Marker oder Polygon verarbeitet wurde
			if ((e as any).originalEvent?._markerHandled) return;
			if ((e as any).originalEvent?._polygonHandled) return;
			const clickLatLng = e.latlng;
			for (const polygon of this.polygons) {
				const inside = polygon.polygons.some(ring => isPointInPolygon(clickLatLng, ring));
				if (inside) {
					this.openPolygonContextMenu(polygon);
					return;
				}
			}
		});

		// Touch-Geräte: BoxZoom deaktivieren (zeichnet sonst blauen Rahmen beim Panning)
		if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
			this.map.boxZoom?.disable();
		}

		// Patch TouchZoom für schnelleres Pinch-Zooming
		this.patchTouchZoom();

		// Prioritärer Klick-Handler: Punkte haben Vorrang vor anderen Vektor-Layern
		// Verwendet Capture-Phase um vor allen anderen Handlern ausgeführt zu werden
		this.map.getContainer().addEventListener('click', (e: MouseEvent) => this.handlePriorityPointClick(e), true);

		// WMS-Info-Tool initialisieren
		this.wmsInfoService.init(this.map, () => this.mapLayers);
		this.drawingService.onToolActivated = () => this.wmsInfoService.endWmsInfoMode();

		this.map.on('zoomend', () => {
			this.pointRenderingService.updateScaling();
			this.mapStatusService.currentZoom.set(this.map!.getZoom());
		});
		this.mapStatusService.currentZoom.set(this.map.getZoom());

    const b = this.map.getBounds();

    if ((Control as any).Scale!) {
      const scale = new (Control as any).Scale({
        metric: true,
        imperial: false,
        position: 'bottomleft'
      });
      this.map.addControl(scale);
    }
		
		// Zoom-Control wird jetzt durch MapControlsComponent ersetzt
		// this.map.addControl(this.zoomControl);
		// this._subscribeTo(this.zoomControl.zoomToWindowButtonClicked, "zoomToWindowButtonClicked", () => this.onZoomToWindow());
		// this._subscribeTo(this.zoomControl.gpsHomeButtonClicked, "gpsHomeButtonClicked", () => this.onGpsHome());
		// this._subscribeTo(this.zoomControl.homeButtonClicked, "homeButtonClicked", () => this.onHome());
		// this._subscribeTo(this.zoomControl.selectionButtonClicked, "selectionButtonClicked", () => this.onZoomToSelection());

		// Selection Control wird jetzt durch MapControlsComponent ersetzt
		// this.map.addControl(this.selectionControl);
		// this.selectionControl.setCount(this.selectionService.count());
		// this._subscribeTo(this.selectionControl.modeChanged, "selectionModeChanged", (mode: SelectionMode) => this.onSelectionModeChanged(mode));
		// this._subscribeTo(this.selectionControl.clearClicked, "selectionClearClicked", () => this.selectionService.clear());
		// this._subscribeTo(this.selectionControl.printClicked, "selectionPrintClicked", () => this.onPrintSelection());
		// this._subscribeTo(this.selectionControl.deleteClicked, "selectionDeleteClicked", () => this.onDeleteSelection());
		// this._subscribeTo(this.selectionControl.finishPolygonClicked, "selectionFinishPolygon", () => this.finishPolygonSelection());
		
    // Layer immer laden wenn Map bereit ist
    // layersChanged könnte vor onMapReady gefeuert haben (Race Condition beim Start)
    this.scheduleLayerChange = false;
    this.onLayersChanged();
    if (this.schedulePointsChange) this.onPointsChanged();
    if (this.schedulePolygonsChange) this.onPolygonsChanged();
    if (this.scheduleDraggablePointChange) this.onDraggablePointChanged();
    let bnds = new LatLngBounds([53.566414, 14.622803], [53.720279, 13.577728]); // MV
    const intersect = (b1: LatLngBounds, b2: LatLngBounds) => {
      return new LatLngBounds(
        [ Math.max(b1.getWest(), b2.getWest()), Math.max(b1.getSouth(), b2.getSouth()) ],
        [ Math.min(b1.getEast(), b2.getEast()), Math.min(b1.getNorth(), b2.getNorth()) ]);
    };
    this.ls.mapDef().layers.forEach((ld: LayerDetails) => {
      if (ld.bbox) {
        const ldbbox = new LatLngBounds((ld.bbox as any)._southWest, (ld.bbox as any)._northEast);
        if (bnds.intersects(ldbbox))
          bnds = intersect(bnds, ldbbox);
      }
    });

		this.onAuthChanged();

		// Aktuelle Projekt-Punkt-IDs vom Service übernehmen (wichtig bei Rückkehr zur Karte)
		if (this.pps.isInitialized()) {
			this.pointRenderingService.projectPointIds = this.pps.allProjectPointIds();
			this.pointRenderingService.assignedPointIds = new Set(this.pps.assignedPointIds());
		}
  }

	private initEditControls() {
		if (!this.map || !(this.map as any)._controlCorners) return;
		if (!this.boxControl) {
			// Box Selection Control hinzufügen
			this.boxControl = new BoxSelectionControl({ position: 'topright' });
			this.boxControl.addTo(this.map);
			this.mapSelectionService.setBoxControl(this.boxControl);

			// Event Listener für ausgewählte Bounds
			this.boxControl.boundsSelected.subscribe((bounds: L.LatLngBounds) => {
				console.log('Ausgewählte Bounds:', bounds);
			});
		}

		if (!this.polygonControl) {
			// Polygon Selection Control hinzufügen
			this.polygonControl = new PolygonSelectionControl({ position: 'topright' });
			this.polygonControl.addTo(this.map);
		}

		// Draw-Toolbar wird bei Bedarf in startZeichnungslayerEdit() erstellt

		// TODO: Download neu implementieren
		// this._subscribeTo(this.layersControl.download, 'download', (what: string) => {
		// 	const plusLevels = Math.min(Math.max(parseInt(this.storage.getSync('offlinePlusLevels') || '0'), 0), 2) as 0|1|2;
		// 	switch (what) {
		// 		case 'Zeichnungslayer':
		// 			this.ps.getProjectValue("zeichnungslayer").then(value => {
		// 				this.ls.saveJSONToFile(value, "Zeichnungslayer.geojson");
		// 			});
		// 			break;
		// 		case 'Punkte':
		// 			if (this.points) this.ls.saveJSONToFile(this.ls.pointsToJson(this.points), "Punkte.geojson");
		// 			break;
		// 		case '':
		// 			this.onSeedRequest(plusLevels);
		// 			break;
		// 		default:
		// 			this.onSeedRequest(plusLevels, this.mapLayers[what]);
		// 			break;
		// 	}
		// });

		if (this.drawingService.zeichnungenLinien)
			this._addLayerToMap('Zeichnungen_Linien', this.drawingService.zeichnungenLinien, true);
		if (this.drawingService.zeichnungenPolygone)
			this._addLayerToMap('Zeichnungen_Polygone', this.drawingService.zeichnungenPolygone, true);
		
		if (this.ps.canI('use', 'filter')) {
			// Gespeicherten Filter wiederherstellen
			this.filterCoordinationService.restoreCurrentFilter();
		}
	}

  private zoomToGrid(): void {
    if (!this.map) return;

    // Grid-Layer aus mapLayers holen
    const layer = this.mapLayers['Punktgitter'] as any;
    if (!layer) return;

    // Bounds vom Layer holen
    if (typeof layer.getBounds === 'function') {
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }

  /**
   * Setzt die Sichtbarkeit des Punkte-Layers (inkl. Labels)
   */
  private setPointsLayerVisibility(visible: boolean): void {
    if (!this.map) return;

    if (visible) {
      if (this.pointRenderingService.pointsLayer && !this.map.hasLayer(this.pointRenderingService.pointsLayer)) {
        this.pointRenderingService.pointsLayer.addTo(this.map);
      }
      // Labels neu erstellen (wurden beim Ausblenden entfernt)
      this.pointRenderingService.updatePointLabelLayer();
      // GPKG-Labels neu rendern (Kollisionssituation hat sich geändert)
      // Kurze Verzögerung damit Punkt-Labels zuerst gerendert werden
      setTimeout(() => this.gpkgRenderingService.refreshGpkgLabels(), 20);
    } else {
      if (this.pointRenderingService.pointsLayer && this.map.hasLayer(this.pointRenderingService.pointsLayer)) {
        this.map.removeLayer(this.pointRenderingService.pointsLayer);
      }
      if (this.pointRenderingService.pointLabelLayer && this.map.hasLayer(this.pointRenderingService.pointLabelLayer)) {
        this.map.removeLayer(this.pointRenderingService.pointLabelLayer);
      }
      // Punkt-Label-Rechtecke leeren
      this.pointRenderingService.pointLabelRects.length = 0;
      // GPKG-Labels neu rendern (keine Punkt-Kollisionen mehr)
      this.gpkgRenderingService.refreshGpkgLabels();
    }
  }

  private setPolygonsLayerVisibility(visible: boolean): void {
    if (!this.map) return;
    const polygonsLayer = this.polygonRenderingService.polygonsLayer;
    const labelLayer = this.polygonRenderingService.polygonLabelLayer;

    if (visible) {
      if (polygonsLayer && !this.map.hasLayer(polygonsLayer)) {
        polygonsLayer.addTo(this.map);
      }
      this.polygonRenderingService.updatePolygonLabelLayer();
    } else {
      if (polygonsLayer && this.map.hasLayer(polygonsLayer)) {
        this.map.removeLayer(polygonsLayer);
      }
      if (labelLayer && this.map.hasLayer(labelLayer)) {
        this.map.removeLayer(labelLayer);
      }
    }
  }




  onMapZoomEnd(e: LeafletEvent) {
    if (!this.mapStatus) return;
    this.mapStatus.zoom = e.target.getZoom();
    this.mapStatus.center = e.target.getCenter();
    this.mapStatus.bounds = e.target.getBounds();
		if (this.persistentBounds) this.mapStatusService.saveGlobalMapState(this.mapStatus);
  }

  updateAttachments() {
    this.ls.layersChanged.emit();
  }


  onRemoveAttachment(a: string) {
    this.storage.removeProjectValue(a, this.ps.project(), 'attachments');
    // Geodatendatei aus Projekt-Dokument entfernen
    this.ps.removeGeodataFile(a);
    while (true) {
			const md = this.ls.mapDef();
      const idx = md.layers.findIndex((ld: LayerDetails) => ld.name.endsWith(`(${a})`));
      if (idx === undefined || idx === -1) break;
			md.layers.splice(idx, 1);
      this.ls.mapDef.set(md);
    }
    this.updateAttachments();
  }
	
	onHome() {
		const layerGroupBounds = (lg?: LayerGroup<any>): LatLngBounds|undefined => {
			if (!lg) return;
			let bnds: LatLngBounds|undefined = undefined;
			lg.eachLayer((l: Layer) => {
				const layer = l as any;
				if (layer.getLatLng) {
					const latlng = layer.getLatLng();
					bnds = bnds ? bnds.extend(latlng) : L.latLngBounds(latlng, latlng);
				}
				if (layer.getBounds) {
					const lb = layer.getBounds();
					bnds = bnds ? bnds.extend(lb) : lb;
				}
			});
			return bnds;
		};

		const projectRegionsBounds = (): LatLngBounds|undefined => {
			const regions = this.ls.regions()?.regions;
			if (!regions?.length) return undefined;
			return regions.reduce(
				(acc: LatLngBounds, region: Region) => acc.extend(region.bounds),
				new LatLngBounds(regions[0].bounds.getSouthWest(), regions[0].bounds.getNorthEast())
			);
		};

		let bnds = projectRegionsBounds();
		if (!bnds)
			bnds = layerGroupBounds(this.pointRenderingService.pointsLayer);
		if (!bnds)
			bnds = this.mapStatusService.mapLimits.maxSubBounds;
		this.map?.fitBounds(bnds!, {
			animate: true,
			duration: 1.0,   // Dauer in Sekunden
			easeLinearity: 0.25,
		});
	}
	

	onBoundsRequested() {
		const regions = this.ls.regions();
		const newIndex = regions.regions.length;
		const bnds = this.map.getBounds();
		const newRegion: Region = {
			type: 'bounds',
			bounds: bnds
		};
		regions.regions.push(newRegion);
		this.ls.regions.update(() => cloneDeep(regions));
		// Neuen Bereich automatisch aktivieren
		this.pps.activeRegionIndices.update(current => [...current, newIndex]);
		this.map.fitBounds(bnds.pad(0.01));
	}

	onCustomBoundsRequested() {
		if (!this.boxControl) return;
		this.boxControl.startSelection();

		const cleanup = () => {
			boundsSubscription.unsubscribe();
			cancelledSubscription.unsubscribe();
		};

		const boundsSubscription = this.boxControl.boundsSelected.subscribe((bounds: L.LatLngBounds) => {
			const regions = this.ls.regions();
			const newIndex = regions.regions.length;
			const newRegion: Region = {
				type: 'bounds',
				bounds: bounds
			};
			regions.regions.push(newRegion);
			const updatedRegions = cloneDeep(regions);
			this.ls.regions.update(() => updatedRegions);
			this.ps.setProjectValue('regions', updatedRegions);
			this.ls.regionsChanged.emit();
			// Neuen Bereich automatisch aktivieren
			this.pps.activeRegionIndices.update(current => [...current, newIndex]);
			// Modal wieder öffnen
			this.ls.regionDrawingCompleted.emit();
			cleanup();
		});

		const cancelledSubscription = this.boxControl.selectionCancelled.subscribe(() => {
			// Bei Abbruch oder zu kleinem Rechteck: Modal wieder öffnen
			this.ls.regionDrawingCompleted.emit();
			cleanup();
		});
	}

	onPolygonRequested() {
		if (!this.polygonControl) return;
		this.polygonControl.startSelection();

		const cleanup = () => {
			polygonSubscription.unsubscribe();
			cancelledSubscription.unsubscribe();
		};

		const polygonSubscription = this.polygonControl.polygonSelected.subscribe((polygon: LatLng[]) => {
			const regions = this.ls.regions();
			const newIndex = regions.regions.length;
			const newRegion: Region = {
				type: 'polygon',
				bounds: getBoundsForPolygon(polygon),
				polygon: polygon
			};
			regions.regions.push(newRegion);
			const updatedRegions = cloneDeep(regions);
			this.ls.regions.update(() => updatedRegions);
			this.ps.setProjectValue('regions', updatedRegions);
			this.ls.regionsChanged.emit();
			// Neuen Bereich automatisch aktivieren
			this.pps.activeRegionIndices.update(current => [...current, newIndex]);
			// Modal wieder öffnen
			this.ls.regionDrawingCompleted.emit();
			cleanup();
		});

		const cancelledSubscription = this.polygonControl.selectionCancelled.subscribe(() => {
			// Bei Abbruch: Modal wieder öffnen
			this.ls.regionDrawingCompleted.emit();
			cleanup();
		});
	}

	// onFreehandRegionRequested, createRegionFromPolygon, hideAllControls, showAllControls → FreehandRegionService

	async seedSingleLayer(layerName: string): Promise<void> {
		let layer = this.mapLayers[layerName];

		// Falls Layer noch nicht geladen, warten und erneut versuchen
		if (!layer) {
			// Warte bis Layer-Loading abgeschlossen ist
			let attempts = 0;
			const maxAttempts = 50; // max 5 Sekunden warten
			while (!layer && attempts < maxAttempts) {
				await new Promise(resolve => setTimeout(resolve, 100));
				layer = this.mapLayers[layerName];
				attempts++;
			}

			if (!layer) {
				console.warn(`Layer "${layerName}" nicht gefunden in mapLayers nach ${maxAttempts * 100}ms`);
				return;
			}
		}

		const plusLevelsStr = await this.storage.get('offlinePlusLevels');
		const plusLevels = Math.min(Math.max(parseInt(plusLevelsStr || '0'), 0), 2) as 0|1|2;
		await this.os.seedAllLayers(plusLevels, this.mapLayers, this.mapStatus, () => {}, layer);
	}
	
	onRegionsChanged() {
		if (!this.map || !this.regions) return;

		// Debounce: Mehrfache schnelle Aufrufe zusammenfassen
		if (this.regionsChangeTimeout) {
			clearTimeout(this.regionsChangeTimeout);
		}
		this.regionsChangeTimeout = setTimeout(() => {
			this.regionsChangeTimeout = null;
			this._doRegionsChanged();
		}, 100);
	}

	private async _doRegionsChanged() {
		if (!this.map || !this.regions) return;

		this.regions.clearLayers();

		// Region-Namen laden (nur wenn Projekt aktiv)
		let regionNames: Record<number, string> = {};
		if (this.ps.project()) {
			try {
				const projectPoints = await this.ps.getProjectValue('projectPoints') as any;
				regionNames = projectPoints?.regionNames ?? {};
			} catch {
				// Ignorieren wenn kein Projekt aktiv
			}
		}

		// Pane für Projektbereiche erstellen (über allen anderen Layern)
		if (!this.map.getPane('projektbereiche')) {
			this.map.createPane('projektbereiche');
			this.map.getPane('projektbereiche')!.style.zIndex = '650';
		}

		// Pane für Negativbereich (unter den Projektbereichen)
		if (!this.map.getPane('projektbereiche-negativ')) {
			this.map.createPane('projektbereiche-negativ');
			this.map.getPane('projektbereiche-negativ')!.style.zIndex = '649';
		}

		const allRegions = this.ls.regions().regions;

		// Negativbereich: Weiße transparente Fläche außerhalb der Bereiche
		if (allRegions.length > 0) {
			// Großes Welt-Polygon (äußerer Ring, gegen den Uhrzeigersinn)
			const worldBounds: L.LatLngTuple[] = [
				[-90, -180],
				[-90, 180],
				[90, 180],
				[90, -180],
				[-90, -180]
			];

			// Holes: Die Bereiche als Löcher (im Uhrzeigersinn)
			const holes: L.LatLngTuple[][] = allRegions.map(region => {
				if (region.type === 'polygon' && region.polygon) {
					// Polygon-Koordinaten
					return region.polygon.map(p => [p.lat, p.lng] as L.LatLngTuple);
				} else {
					// Rechteck zu Polygon konvertieren (im Uhrzeigersinn)
					const sw = region.bounds.getSouthWest();
					const ne = region.bounds.getNorthEast();
					return [
						[sw.lat, sw.lng],
						[sw.lat, ne.lng],
						[ne.lat, ne.lng],
						[ne.lat, sw.lng],
						[sw.lat, sw.lng]
					] as L.LatLngTuple[];
				}
			});

			// Polygon mit Löchern erstellen
			const invertedPolygon = L.polygon([worldBounds, ...holes], {
				color: 'transparent',
				weight: 0,
				fillColor: '#ffffff',
				fillOpacity: 0.25,
				interactive: false,
				pane: 'projektbereiche-negativ'
			});
			this.regions?.addLayer(invertedPolygon);
		}

		allRegions.forEach((region: Region, index: number) => {
			let labelPosition: L.LatLng;

			if (region.type === 'polygon' && region.polygon) {
				// Polygon hinzufügen
				this.regions?.addLayer(L.polygon(region.polygon, {
					color: '#000',
					weight: 1,
					fillColor: 'transparent',
					interactive: false,
					pane: 'projektbereiche'
				}));

				// Label-Position: Westlichster Punkt des Polygons
				labelPosition = region.polygon.reduce((westernmost, current) =>
					current.lng < westernmost.lng ? current : westernmost
				);
			} else {
				// Rechteck hinzufügen
				this.regions?.addLayer(L.rectangle(region.bounds, {
					color: '#000',
					weight: 1,
					fillColor: 'transparent',
					interactive: false,
					pane: 'projektbereiche'
				}));

				// Label-Position: Obere linke Ecke des Rechtecks
				labelPosition = region.bounds.getNorthWest();
			}

			// Name-Label hinzufügen
			const name = regionNames[index] || regionNames[String(index) as any] || `Bereich ${index + 1}`;
			const labelIcon = L.divIcon({
				className: 'region-label',
				html: `<span style="background: rgba(255,255,255,0.8); padding: 3px 6px; font-size: 11px; border-radius: 3px; white-space: nowrap;">${name}</span>`,
				iconAnchor: [-4, -4]  // Nach rechts und unten vom Eckpunkt verschoben
			});
			const labelMarker = L.marker(labelPosition, {
				icon: labelIcon,
				interactive: false,
				pane: 'projektbereiche'
			});
			this.regions?.addLayer(labelMarker);
		});

		// Auch das verdunkelte Overlay außerhalb der Projektbereiche aktualisieren
		this._updateProjectArea();
	}
	
	onRegionClicked(r: LatLngBounds) {
		// Prüfen ob Map existiert und vollständig initialisiert ist
		if (!this.map || !this.map.getContainer() || !(this.map as any)._loaded) {
			// Map nicht bereit - Request verwerfen (wird beim nächsten Projekt-Wechsel erneut getriggert)
			return;
		}
		try {
			this.map.fitBounds(r.pad(0.2));
		} catch (e) {
			// Map in ungültigem Zustand - nicht erneut versuchen, da dies zu Endlosschleifen führt
			console.warn('fitBounds fehlgeschlagen - Map möglicherweise während Navigation ungültig', e);
		}
	}

	onZoomToLayer(layerName: string) {
		const ld = this.ls.mapDef()?.layers.find(l => l.name === layerName);
		if (!ld?.bbox) return;

		// bbox kann als serialisiertes Objekt vorliegen, daher rekonstruieren
		const bbox = ld.bbox;
		const bounds = new LatLngBounds(
			(bbox as any)._southWest || bbox.getSouthWest(),
			(bbox as any)._northEast || bbox.getNorthEast()
		);

		if (bounds.isValid()) {
			this.map.fitBounds(bounds.pad(0.05));
		}
	}

	onPointBoundsRequested() {
		if (!this.boxControl) return;

		// ProjectArea während Selektion ausblenden
		this.hideProjectArea();

		this.boxControl.startSelection();
		const s = this.boxControl.boundsSelected.subscribe((bounds: LatLngBounds) => {
			// Punkte in Bounds finden
			const pointsInBounds = this.points?.filter(point => {
				if (!point.latLng) return false;
				return bounds.contains(point.latLng);
			}) ?? [];
			// Event mit Punkt-IDs emittieren
			this.ls.pointsSelectedFromBounds.emit(pointsInBounds.map(point => point.id));

			// ProjectArea wieder einblenden
			this.showProjectArea();
			s.unsubscribe();
		});
	}

	onProjectPointsChanged(ids: Set<string>) {
		this.pointRenderingService.projectPointIds = ids;
		this.onPointsChanged();
		this._updateProjectArea();
	}

	onAssignedPointsChanged(ids: Set<string>) {
		this.pointRenderingService.assignedPointIds = ids;
		this.onPointsChanged();
	}

	private _getLayerBounds(layer: any): LatLngBounds | undefined {
		if (!layer) return undefined;
		if (!layer.getBounds && !layer.getLatLng && !layer.eachLayer)
			return undefined;

		const boundsFromSingleLayer = (l: any) => {
			let bnds: LatLngBounds | undefined = undefined;
			if (l.getBounds) {
				bnds = l.getBounds();
			}
			if (l.getLatLng) {
				const latlng = l.getLatLng();
				bnds = bnds ? bnds.extend(latlng) : latLngBounds(latlng, latlng);
			}
			return bnds;
		};

		let bounds = boundsFromSingleLayer(layer);
		if (layer.eachLayer) {
			layer.eachLayer((l: any) => {
				const layerBounds = boundsFromSingleLayer(l);
				if (!layerBounds) return;
				bounds = bounds ? bounds.extend(layerBounds) : layerBounds;
			});
		}
		return bounds;
	}

	async onGpsHome() {
		// iOS 13+: Kompass-Permission anfordern (muss bei User-Interaktion passieren)
		await this.gps.requestCompassPermission();

		const bnds = this.mapStatusService.mapLimits.maxSubBounds;
		const ll = this.currentPosition.getLatLng();
		if (!bnds.contains(ll)) {
			this.map.setMaxBounds(bnds.extend(ll));
		}
		this.map.panTo(ll, {
			animate: true,
			duration: 1.0,   // Dauer in Sekunden
			easeLinearity: 0.25,
		});
	}

	onZoomToSelection() {
		const selectedIds = this.selectionService.getSelection();
		if (selectedIds.size === 0) return;

		let bnds: LatLngBounds | undefined = undefined;
		this.points?.forEach((point: MapMarker) => {
			if (!selectedIds.has(point.id)) return;
			if (!point.latLng) return;
			bnds = bnds ? bnds.extend(point.latLng) : L.latLngBounds(point.latLng, point.latLng);
		});

		if (bnds) {
			this.map?.fitBounds(bnds, {
				animate: true,
				duration: 1.0,
				easeLinearity: 0.25,
				padding: [100, 100]
			});
		}
	}

	onZoomToFilter() {
		if (!this.pointsFilter || this.filteredCount === 0) return;

		let bnds: LatLngBounds | undefined = undefined;
		this.points?.forEach((point: MapMarker, index: number) => {
			if (!this.pointsFilter![index]) return;
			if (!point.latLng) return;
			bnds = bnds ? bnds.extend(point.latLng) : L.latLngBounds(point.latLng, point.latLng);
		});

		if (bnds) {
			this.map?.fitBounds(bnds, {
				animate: true,
				duration: 1.0,
				easeLinearity: 0.25,
				padding: [100, 100]
			});
		}
	}

	// onFilter, updateProjectGlobalFilter, onFilterReset, restoreCurrentFilter,
	// onFilterRefresh, onFilterScopeChanged, applyFilterWithCurrentScope → FilterCoordinationService

	onFilter() { this.filterCoordinationService.onFilter(); }
	onFilterRefresh() { this.filterCoordinationService.onFilterRefresh(); }
	onFilterScopeChanged() { this.filterCoordinationService.onFilterScopeChanged(); }

  // Panel Drag & Drop
  onPanelDragStart(event: MouseEvent, panel: HTMLElement) {
    event.preventDefault();
    this.draggingPanel = panel;
    const rect = panel.getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };

    const onMouseMove = (e: MouseEvent) => this.onPanelDrag(e);
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      this.draggingPanel = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  onPanelTouchStart(event: TouchEvent, panel: HTMLElement) {
    if (event.touches.length !== 1) return;
    event.preventDefault();
    this.draggingPanel = panel;
    const touch = event.touches[0];
    const rect = panel.getBoundingClientRect();
    this.dragOffset = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      this.updatePanelPosition(t.clientX, t.clientY);
    };
    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      this.draggingPanel = null;
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }

  private onPanelDrag(event: MouseEvent) {
    this.updatePanelPosition(event.clientX, event.clientY);
  }

  private updatePanelPosition(clientX: number, clientY: number) {
    if (!this.draggingPanel || !this.mapDiv) return;

    const containerRect = this.mapDiv.nativeElement.parentElement?.getBoundingClientRect();
    if (!containerRect) return;

    const panelRect = this.draggingPanel.getBoundingClientRect();

    // Neue Position berechnen (relativ zum Container)
    let newX = clientX - containerRect.left - this.dragOffset.x;
    let newY = clientY - containerRect.top - this.dragOffset.y;

    // Grenzen einhalten
    newX = Math.max(0, Math.min(newX, containerRect.width - panelRect.width));
    newY = Math.max(0, Math.min(newY, containerRect.height - panelRect.height));

    // Position aktualisieren
    if (this.draggingPanel.classList.contains('project-panel')) {
      // Bestimmen welches Panel es ist
      const isLayersPanel = this.draggingPanel.querySelector('bp-project-manager[layersOnly]') !== null;
      if (isLayersPanel) {
        this.layersPanelPosition = { x: newX, y: newY };
      } else {
        this.panelPosition = { x: newX, y: newY };
      }
    }
  }

	// ==================== SELECTION METHODS ====================

	private onSelectionChanged() {
		// selectionControl?.setCount wird beibehalten falls Control noch aktiv ist
		this.selectionControl?.setCount(this.selectionService.count());
		this.pointRenderingService.updateSelectionHighlighting();
		this.pointRenderingService.updateClusterIcons();
	}

	// updateSelectionHighlighting, updateClusterIcons → PointRenderingService






	private async onDeleteSelection() {
		if (!this.projectHandler) return;
		const count = this.selectionService.count();
		if (count === 0) return;

		const confirmed = await this.confirmService.confirmDelete(
			`Sollen wirklich ${count} Punkte gelöscht werden?`
		);
		if (!confirmed) return;

		const ids = this.selectionService.getSelectionArray();
		const result = await this.projectHandler.deletePoints(ids);
		if (result.success) {
			this.selectionService.clear();
			this.pointDeleted.emit();
		}
	}

	private onPrintSelection() {
		const count = this.selectionService.count();
		if (count === 0) return;

		this.navigationHandler?.navigateToPrintView(this.selectionService.getSelectionArray());
	}

	// === Public methods for MapControlsComponent ===

	onSelectionModeChange(mode: SelectionMode): void {
		this.mapSelectionService.onSelectionModeChanged(mode);
	}

	clearSelection(): void {
		this.selectionService.clear();
	}

	selectFiltered(): void {
		if (!this.pointsFilter || !this.points) return;
		const filteredIds: string[] = [];
		this.points.forEach((point, index) => {
			if (this.pointsFilter![index]) {
				filteredIds.push(point.id);
			}
		});
		if (filteredIds.length > 0) {
			this.selectionService.add(filteredIds);
		}
	}

	printSelection(): void {
		this.onPrintSelection();
	}

	deleteSelection(): void {
		this.onDeleteSelection();
	}
	// #endregion Punkt-Methoden

	// ==================== WMS INFO TOOL ====================

	// WMS-Info-Panel per Drag verschieben
	onWmsInfoDragStart(event: MouseEvent | TouchEvent): void {
		// Close-Button nicht als Drag behandeln
		if ((event.target as HTMLElement).closest('.context-menu-close')) return;
		const panel = (event.target as HTMLElement).closest('.wms-info-panel') as HTMLElement;
		if (!panel) return;
		event.preventDefault();

		const startX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
		const startY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
		const rect = panel.getBoundingClientRect();
		const offsetX = startX - rect.left;
		const offsetY = startY - rect.top;

		const onMove = (e: MouseEvent | TouchEvent) => {
			const cx = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
			const cy = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
			// transform: translate(-50%, 10px) muss berücksichtigt werden
			panel.style.left = (cx - offsetX + rect.width / 2) + 'px';
			panel.style.top = (cy - offsetY - 10) + 'px';
		};

		const onEnd = () => {
			document.removeEventListener('mousemove', onMove);
			document.removeEventListener('mouseup', onEnd);
			document.removeEventListener('touchmove', onMove);
			document.removeEventListener('touchend', onEnd);
		};

		document.addEventListener('mousemove', onMove);
		document.addEventListener('mouseup', onEnd);
		document.addEventListener('touchmove', onMove, { passive: false });
		document.addEventListener('touchend', onEnd);
	}

	onToggleWmsInfo(): void {
		if (this.wmsInfoService.wmsInfoActive) {
			this.wmsInfoService.endWmsInfoMode();
		} else {
			this.drawingService.onDeactivateAllTools();
			this.wmsInfoService.startWmsInfoMode();
		}
	}

	onDeactivateAllTools(): void {
		this.drawingService.onDeactivateAllTools();
		this.wmsInfoService.endWmsInfoMode();
	}

	// Außengrenzen von Mecklenburg-Vorpommern
	private readonly MV_BOUNDS = L.latLngBounds(
		L.latLng(53.1, 10.6),  // Südwest
		L.latLng(54.7, 14.5)   // Nordost
	);

	onFitBounds(): void {
		const hasProject = !!this.ps.project();

		// 1. Projekt geladen: Auf Bearbeitungsbereich (Projekt-Regions) zoomen
		if (hasProject) {
			const regions = this.ls.regions()?.regions;
			if (regions?.length) {
				const bounds = regions.reduce(
					(acc: L.LatLngBounds | undefined, r) => acc ? acc.extend(r.bounds) : r.bounds,
					undefined as L.LatLngBounds | undefined
				);
				if (bounds) {
					this.map?.fitBounds(bounds, { animate: true, duration: 1.0 });
					return;
				}
			}
		}

		// 2. Fallback: Mecklenburg-Vorpommern
		this.map?.fitBounds(this.MV_BOUNDS, { animate: true, duration: 1.0 });
	}

	/**
	 * Patcht den TouchZoom-Handler für Pinch-Zooming.
	 * Multiplier 1.0 = Standard-Geschwindigkeit (war 2.0, das war zu schnell/instabil)
	 */
	private patchTouchZoom(): void {
		const touchZoom = (this.map as any).touchZoom;
		if (!touchZoom) return;

		const zoomMultiplier = 1.0;  // Standard-Geschwindigkeit (1.0 = normal)

		touchZoom._onTouchMove = function(e: TouchEvent) {
			if (!e.touches || e.touches.length !== 2 || !this._zooming) { return; }

			const map = this._map;
			const p1 = map.mouseEventToContainerPoint(e.touches[0]);
			const p2 = map.mouseEventToContainerPoint(e.touches[1]);
			const currentDist = p1.distanceTo(p2);

			// Scale berechnen und mit Multiplikator verstärken
			let scale = currentDist / this._startDist;

			// Verstärkung: Abweichung von 1.0 multiplizieren
			const delta = scale - 1;
			scale = 1 + (delta * zoomMultiplier);

			this._zoom = map.getScaleZoom(scale, this._startZoom);

			if (!map.options.bounceAtZoomLimits && (
				(this._zoom < map.getMinZoom() && scale < 1) ||
				(this._zoom > map.getMaxZoom() && scale > 1))) {
				this._zoom = map._limitZoom(this._zoom);
			}

			if (map.options.touchZoom === 'center') {
				this._center = this._startLatLng;
				if (scale === 1) { return; }
			} else {
				const centerDelta = p1._add(p2)._divideBy(2)._subtract(this._centerPoint);
				if (scale === 1 && centerDelta.x === 0 && centerDelta.y === 0) { return; }
				this._center = map.unproject(map.project(this._pinchStartLatLng, this._zoom).subtract(centerDelta), this._zoom);
			}

			if (!this._moved) {
				map._moveStart(true, false);
				this._moved = true;
			}

			L.Util.cancelAnimFrame(this._animRequest);
			const moveFn = L.Util.bind(map._move, map, this._center, this._zoom, {pinch: true, round: false}, undefined);
			this._animRequest = L.Util.requestAnimFrame(moveFn, this, true);

			L.DomEvent.preventDefault(e);
		};
	}
}
